import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  PersonOff as PersonOffIcon,
  PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useSWR, { mutate as globalMutate } from 'swr';
import { tenantUsers, type TenantUser, type TenantUserRole, type TenantUserUpsertBody } from '@/api/tenantUsers';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DataTable } from '@/components/DataTable';
import { StatusPill } from '@/components/StatusPill';
import { useToast } from '@/components/Toaster';
import { PageShell } from '@/layout/PageShell';
import { relativeTime } from '@/utils/format';

/**
 * Tenant Users page — list / add / edit / deactivate users under the
 * current tenant. Backed by `/api/abdm/tenant/users`, which is scoped
 * server-side to the tenantId on the request, so a TENANT_ADMIN can
 * only see and mutate users inside their own tenant.
 *
 * Allowed roles when creating: TENANT_ADMIN, CLINIC_USER, TENANT_USER.
 * PLATFORM_ADMIN is reserved for the platform owner.
 */

const ROLES: TenantUserRole[] = ['TENANT_ADMIN', 'CLINIC_USER', 'TENANT_USER'];

export function UsersPage() {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TenantUser | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<TenantUser | null>(null);

  const { data, error, isLoading } = useSWR('/api/abdm/tenant/users', () => tenantUsers.list());
  const refresh = () => globalMutate('/api/abdm/tenant/users');

  return (
    <PageShell
      kicker="TENANT · USERS"
      title="Users"
      subtitle="Add, edit, and disable users under your tenant. Tenant admins can manage clinic and tenant-user logins."
      width="wide"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          Add user
        </Button>
      }
    >
      <DataTable<TenantUser>
        rows={data}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        searchable
        searchPlaceholder="username, email, name…"
        paginated
        empty={{
          icon: PeopleAltIcon,
          title: 'No users yet.',
          description: 'Add the first user to grant access to your tenant.',
          action: <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>Add user</Button>,
        }}
        columns={[
          {
            key: 'username',
            header: 'Username',
            sortable: true,
            sortValue: (r) => r.username,
            pinned: true,
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={500}>{r.username}</Typography>
                {r.fullName && <Typography variant="caption" color="text.tertiary">{r.fullName}</Typography>}
              </Stack>
            ),
          },
          {
            key: 'email',
            header: 'Email',
            sortable: true,
            sortValue: (r) => r.email ?? '',
            cell: (r) => <Typography variant="caption">{r.email ?? '—'}</Typography>,
          },
          {
            key: 'mobile',
            header: 'Mobile',
            sortable: true,
            sortValue: (r) => r.mobile ?? '',
            cell: (r) => <Typography variant="caption">{r.mobile ?? '—'}</Typography>,
            width: 140,
          },
          {
            key: 'role',
            header: 'Role',
            sortable: true,
            sortValue: (r) => r.role,
            filter: { type: 'select', getFilterValue: (r) => r.role },
            cell: (r) => <StatusPill tone={r.role === 'TENANT_ADMIN' ? 'info' : 'mono'} dot={false}>{r.role}</StatusPill>,
            width: 150,
          },
          {
            key: 'status',
            header: 'Status',
            sortable: true,
            sortValue: (r) => (r.active && r.enabled ? 1 : 0),
            filter: {
              type: 'select',
              options: [{ label: 'Active', value: 'active' }, { label: 'Disabled', value: 'disabled' }],
              getFilterValue: (r) => (r.active && r.enabled ? 'active' : 'disabled'),
            },
            cell: (r) => (r.active && r.enabled
              ? <StatusPill tone="success">Active</StatusPill>
              : <StatusPill tone="mono">Disabled</StatusPill>),
            width: 110,
          },
          {
            key: 'updated',
            header: 'Updated',
            sortable: true,
            sortValue: (r) => r.updatedAt ?? '',
            cell: (r) => <Typography variant="caption" color="text.secondary">{relativeTime(r.updatedAt)}</Typography>,
            width: 130,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            width: 180,
            pinned: true,
            cell: (r) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="Edit user">
                  <IconButton size="small" onClick={() => setEditing(r)} aria-label="edit user">
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {r.active && r.enabled && (
                  <Tooltip title="Disable user">
                    <IconButton size="small" color="error" onClick={() => setPendingDeactivate(r)} aria-label="disable user">
                      <PersonOffIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ),
          },
        ]}
      />

      <UserFormDialog
        open={creating || !!editing}
        initial={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); refresh(); }}
      />

      <ConfirmDialog
        open={!!pendingDeactivate}
        onClose={() => setPendingDeactivate(null)}
        title="Disable this user?"
        body={pendingDeactivate ? (
          <Typography variant="body2">
            <strong>{pendingDeactivate.username}</strong> will no longer be able to sign in. Their history is preserved — re-enable from the edit dialog.
          </Typography>
        ) : null}
        actionLabel="Disable"
        tone="danger"
        onConfirm={async () => {
          if (!pendingDeactivate) return;
          await tenantUsers.deactivate(pendingDeactivate.id);
          setPendingDeactivate(null);
          refresh();
        }}
      />
    </PageShell>
  );
}

// -------------------- Add / Edit user form --------------------

interface UserForm {
  username: string;
  password: string;
  role: TenantUserRole;
  email: string;
  mobile: string;
  fullName: string;
  active: boolean;
  enabled: boolean;
}

function UserFormDialog({
  open,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial: TenantUser | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const isEdit = !!initial;

  const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm<UserForm>({
    defaultValues: blankForm(),
  });

  useEffect(() => {
    if (open) reset(formFromUser(initial));
  }, [open, initial, reset]);

  const onSubmit = async (form: UserForm) => {
    try {
      const body: TenantUserUpsertBody = {
        username: form.username.trim(),
        ...(form.password ? { password: form.password } : {}),
        role: form.role,
        email: form.email.trim() || undefined,
        mobile: form.mobile.trim() || undefined,
        fullName: form.fullName.trim() || undefined,
        active: form.active,
        enabled: form.enabled,
      };
      if (isEdit && initial) {
        await tenantUsers.update(initial.id, body);
        toast.success('User updated', initial.username);
      } else {
        const u = await tenantUsers.create(body);
        toast.success('User added', `${u.username} · ${u.role}`);
      }
      onSaved();
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      toast.error('Save failed', detail?.message ?? detail?.error ?? (e as Error).message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {isEdit ? `Edit ${initial!.username}` : 'Add user'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="user-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Controller
              name="username"
              control={control}
              rules={isEdit ? {} : { required: 'username is required' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Username"
                  required={!isEdit}
                  fullWidth
                  disabled={isEdit}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? (isEdit ? 'username is immutable' : 'used to sign in')}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={isEdit ? {} : { required: 'password is required', minLength: { value: 4, message: 'minimum 4 characters' } }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Password"
                  type="password"
                  required={!isEdit}
                  fullWidth
                  autoComplete="new-password"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? (isEdit ? 'leave blank to keep the current password' : 'shown only at create time')}
                />
              )}
            />
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Role" select fullWidth>
                  {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              )}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => <TextField {...field} type="email" label="Email" fullWidth />}
              />
              <Controller
                name="mobile"
                control={control}
                render={({ field }) => <TextField {...field} label="Mobile" fullWidth />}
              />
            </Box>
            <Controller
              name="fullName"
              control={control}
              render={({ field }) => <TextField {...field} label="Full name" fullWidth />}
            />
            {isEdit && (
              <Stack direction="row" spacing={3} alignItems="center">
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />
                      <Typography variant="body2">Active</Typography>
                    </Stack>
                  )}
                />
                <Controller
                  name="enabled"
                  control={control}
                  render={({ field }) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />
                      <Typography variant="body2">Enabled</Typography>
                    </Stack>
                  )}
                />
              </Stack>
            )}
            <Alert severity="info" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
              Users are scoped to your tenant — they can sign in to the same portal as the tenant admin and act under your tenantId.
            </Alert>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button
          type="submit"
          form="user-form"
          variant="contained"
          disabled={isSubmitting}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add user'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function blankForm(): UserForm {
  return {
    username: '',
    password: '',
    role: 'CLINIC_USER',
    email: '',
    mobile: '',
    fullName: '',
    active: true,
    enabled: true,
  };
}

function formFromUser(u: TenantUser | null): UserForm {
  if (!u) return blankForm();
  return {
    username: u.username,
    password: '',                      // never pre-fill
    role: (ROLES.includes(u.role as TenantUserRole) ? u.role : 'CLINIC_USER') as TenantUserRole,
    email: u.email ?? '',
    mobile: u.mobile ?? '',
    fullName: u.fullName ?? '',
    active: u.active,
    enabled: u.enabled,
  };
}
